/**
 * Primary navigation megamenu: Bootstrap-compatible .show toggles (expedition menu.js + mobile-menu.js).
 * Desktop uses delegation on .site-header-desktop so clicks on label/icon inside the anchor still toggle
 * and always call preventDefault (no data-bs-toggle — theme JS owns the behaviour).
 */

const clearMegamenuL3HostSizing = (root) => {
	const host = root.querySelector('.megamenu-level-three-host');
	if (host) {
		host.style.minHeight = '';
	}
};

const syncMegamenuL3HostSizing = (root) => {
	const host = root.querySelector('.megamenu-level-three-host');
	if (!host) {
		return;
	}
	const visible = host.querySelector('.megamenu-level-three-list.is-visible');
	if (!visible) {
		clearMegamenuL3HostSizing(root);
		return;
	}
	window.requestAnimationFrame(() => {
		const nextHeight = visible.offsetHeight;
		if (nextHeight < 1) {
			return;
		}
		host.style.minHeight = `${nextHeight}px`;
	});
};

const resetDesktopMegamenuFlyouts = () => {
	document.querySelectorAll('.site-header-desktop .js-megamenu-flyout').forEach((root) => {
		root.querySelectorAll('.megamenu-has-children.is-active').forEach((li) => {
			li.classList.remove('is-active');
		});
		root.querySelectorAll('.megamenu-level-three-list.is-visible').forEach((ul) => {
			ul.classList.remove('is-visible');
		});
		clearMegamenuL3HostSizing(root);
	});
};

/**
 * Megamenu panel is not always layout-adjacent to the toggle (e.g. fixed positioning); resolve via the parent li.
 *
 * @param {Element} toggleOrLink
 * @returns {Element | null}
 */
const getDesktopMegamenuPanel = (toggleOrLink) => {
	const li = toggleOrLink.closest('li.has-megamenu');
	if (!li) {
		return null;
	}
	return li.querySelector('.dropdown-menu.megamenu');
};

const closeAllDesktopMegaMenus = () => {
	resetDesktopMegamenuFlyouts();
	document.querySelectorAll('.site-header-desktop .has-megamenu > a.nav-link.top-level').forEach((toggle) => {
		toggle.classList.remove('show');
		toggle.setAttribute('aria-expanded', 'false');
		const panel = getDesktopMegamenuPanel(toggle);
		if (panel) {
			panel.classList.remove('show');
		}
	});
	document.querySelectorAll('.site-header-desktop [data-menu-dropdown-icon]').forEach((icon) => {
		icon.classList.remove('rotate-180');
	});
};

const closeAllMobileMegaMenus = (root = document) => {
	const panel = root.querySelector('.main-mobile-navigation');
	if (!panel) {
		return;
	}
	resetMobileMenuDrilldown(panel);
};

const getMobileNavigationPanel = () => {
	return document.getElementById('mobile-menu-main');
};

const getMobileNavigationButtons = () => {
	return document.querySelectorAll('.mobile-menu-toggle[data-mobile-menu-toggle="main"]');
};

const syncMobileNavigationFocusability = (panel, isOpen) => {
	if (!panel) {
		return;
	}
	const focusable = panel.querySelectorAll(
		'a, button, input, select, textarea, [tabindex], [contenteditable="true"]'
	);
	focusable.forEach((element) => {
		const originalTabindex = element.getAttribute('tabindex');
		if (!Object.prototype.hasOwnProperty.call(element.dataset, 'mobileMenuOriginalTabindex')) {
			element.dataset.mobileMenuOriginalTabindex = originalTabindex === null ? '__none__' : originalTabindex;
		}
		if (isOpen) {
			const saved = element.dataset.mobileMenuOriginalTabindex;
			if (saved === '__none__') {
				element.removeAttribute('tabindex');
			} else {
				element.setAttribute('tabindex', saved);
			}
		} else {
			element.setAttribute('tabindex', '-1');
		}
	});
};

const syncMobileNavigationButtons = (isOpen) => {
	getMobileNavigationButtons().forEach((button) => {
		if (isOpen) {
			button.setAttribute('aria-expanded', 'true');
		} else {
			button.setAttribute('aria-expanded', 'false');
		}
		button.querySelectorAll('.open-button').forEach((el) => {
			if (isOpen) {
				el.classList.add('hidden!');
			} else {
				el.classList.remove('hidden!');
			}
		});
		button.querySelectorAll('.close-button').forEach((el) => {
			if (isOpen) {
				el.classList.remove('hidden!');
			} else {
				el.classList.add('hidden!');
			}
		});
	});
};

const closeMobileNavigation = () => {
	const panel = getMobileNavigationPanel();
	if (!panel) {
		return;
	}
	panel.classList.remove('is-open');
	panel.removeAttribute('data-open');
	panel.setAttribute('aria-hidden', 'true');
	panel.inert = true;
	syncMobileNavigationFocusability(panel, false);
	document.body.classList.remove('overflow-hidden', 'mobile-menu-open');
	closeAllMobileMegaMenus();
	syncMobileNavigationButtons(false);
};

const openMobileNavigation = () => {
	const panel = getMobileNavigationPanel();
	if (!panel) {
		return;
	}
	panel.classList.add('is-open');
	panel.setAttribute('data-open', '');
	panel.setAttribute('aria-hidden', 'false');
	panel.inert = false;
	syncMobileNavigationFocusability(panel, true);
	document.body.classList.add('overflow-hidden', 'mobile-menu-open');
	resetMobileMenuDrilldown(panel);
	document.dispatchEvent(new CustomEvent('close-language-switcher'));
	syncMobileNavigationButtons(true);
};

const bindMobileNavigationToggle = () => {
	const panel = getMobileNavigationPanel();
	if (!panel) {
		return;
	}
	panel.inert = true;
	syncMobileNavigationFocusability(panel, false);
	getMobileNavigationButtons().forEach((button) => {
		if (button.dataset.mobileMenuToggleBound === '1') {
			return;
		}
		button.dataset.mobileMenuToggleBound = '1';
		button.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			if (panel.hasAttribute('data-open')) {
				closeMobileNavigation();
			} else {
				openMobileNavigation();
			}
		});
	});
};

const drilldownViewportResizeObservers = new WeakMap();

const syncMobileMenuDrilldownViewportHeight = (stack, activeIndex) => {
	const viewport = stack.closest('.mobile-menu-drilldown-viewport');
	if (!viewport) {
		return;
	}
	const levels = stack.querySelectorAll('.mobile-menu-drilldown-level');
	const level = levels[activeIndex];
	if (!level) {
		return;
	}
	const targetHeight = Math.max(0, Math.ceil(Math.max(level.scrollHeight, level.getBoundingClientRect().height)));
	viewport.style.height = `${targetHeight}px`;
};

const wireMobileMenuDrilldownViewportResizeObserver = (stack) => {
	if (drilldownViewportResizeObservers.has(stack)) {
		return;
	}
	const ro = new ResizeObserver(() => {
		const levels = stack.querySelectorAll('.mobile-menu-drilldown-level');
		let activeIndex = 0;
		levels.forEach((level, index) => {
			if (level.classList.contains('is-active')) {
				activeIndex = index;
			}
		});
		syncMobileMenuDrilldownViewportHeight(stack, activeIndex);
	});
	ro.observe(stack);
	drilldownViewportResizeObservers.set(stack, ro);
};

const syncMobileMenuDrilldownStackVars = (stack, activeIndex) => {
	if (!stack) {
		return;
	}
	const levels = stack.querySelectorAll('.mobile-menu-drilldown-level');
	const count = levels.length;
	if (!count) {
		return;
	}
	const safeIndex = Math.max(0, Math.min(activeIndex, count - 1));
	levels.forEach((level, index) => {
		level.inert = index !== safeIndex;
	});
	syncMobileMenuDrilldownViewportHeight(stack, safeIndex);
};

const resetMobileMenuDrilldown = (panel) => {
	if (!panel) {
		return;
	}
	const stack = panel.querySelector('.js-mobile-menu-drilldown-stack');
	const levels = panel.querySelectorAll('.js-mobile-menu-drilldown-stack .mobile-menu-drilldown-level');
	if (!levels.length) {
		return;
	}
	levels.forEach((level, index) => {
		level.classList.toggle('is-active', index === 0);
	});
	syncMobileMenuDrilldownStackVars(stack, 0);
	panel.querySelectorAll('.mobile-menu-drilldown-trigger[aria-expanded="true"]').forEach((trigger) => {
		trigger.setAttribute('aria-expanded', 'false');
	});
};

const activateMobileMenuDrilldownPanel = (currentPanel, targetPanelId) => {
	if (!currentPanel || !targetPanelId) {
		return;
	}
	const stack = currentPanel.querySelector('.js-mobile-menu-drilldown-stack');
	if (!stack) {
		return;
	}
	const levels = stack.querySelectorAll('.mobile-menu-drilldown-level');
	if (!levels.length) {
		return;
	}
	const targetLevel = stack.querySelector(`.mobile-menu-drilldown-level[data-panel-id="${targetPanelId}"]`);
	if (!targetLevel) {
		return;
	}
	let targetIndex = -1;
	levels.forEach((level, index) => {
		if (level === targetLevel) {
			targetIndex = index;
		}
	});
	if (targetIndex < 0) {
		return;
	}
	levels.forEach((level) => {
		const isTarget = level === targetLevel;
		level.classList.toggle('is-active', isTarget);
	});
	syncMobileMenuDrilldownStackVars(stack, targetIndex);
	currentPanel.querySelectorAll('.mobile-menu-drilldown-trigger').forEach((trigger) => {
		const expandsTarget = trigger.getAttribute('data-target-panel-id') === targetPanelId;
		trigger.setAttribute('aria-expanded', expandsTarget ? 'true' : 'false');
	});
};

const initMobileMenuDrilldownStacks = () => {
	document.querySelectorAll('.js-mobile-menu-drilldown-stack').forEach((stack) => {
		const levels = stack.querySelectorAll('.mobile-menu-drilldown-level');
		let activeIndex = 0;
		levels.forEach((level, index) => {
			if (level.classList.contains('is-active')) {
				activeIndex = index;
			}
		});
		syncMobileMenuDrilldownStackVars(stack, activeIndex);
		wireMobileMenuDrilldownViewportResizeObserver(stack);
	});
};

const pinHeaders = () => {
	document.querySelectorAll('.js-header').forEach((headerEl) => {
		headerEl.classList.add('js-header--fixed');
	});
};

let currentDesktopToggle = null;

const bindDesktopMegamenuClicks = (desktopHeader) => {
	if (!desktopHeader || desktopHeader.dataset.megamenuDesktopClicksBound === '1') {
		return;
	}
	desktopHeader.dataset.megamenuDesktopClicksBound = '1';
	desktopHeader.addEventListener('click', (event) => {
		if (!(event.target instanceof Element)) {
			return;
		}
		if (event.target.closest('.dropdown-menu.megamenu')) {
			return;
		}
		const li = event.target.closest('li.has-megamenu');
		if (!li || !desktopHeader.contains(li)) {
			return;
		}
		if (!li.closest('ul.menu-main')) {
			return;
		}
		const link = li.querySelector(':scope > a.nav-link.top-level');
		if (!link) {
			return;
		}
		const linkFromEvent = event.target.closest('a.nav-link.top-level');
		if (!linkFromEvent || linkFromEvent !== link) {
			return;
		}
		const panel = getDesktopMegamenuPanel(link);
		if (!panel) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		document.dispatchEvent(new CustomEvent('close-language-switcher'));
		pinHeaders();

		const willOpen = !link.classList.contains('show');
		const icon = li.querySelector('[data-menu-dropdown-icon]');

		if (currentDesktopToggle && currentDesktopToggle !== link) {
			const prevLi = currentDesktopToggle.closest('li');
			const prevIcon = prevLi ? prevLi.querySelector('[data-menu-dropdown-icon]') : null;
			currentDesktopToggle.classList.remove('show');
			currentDesktopToggle.setAttribute('aria-expanded', 'false');
			if (prevIcon) {
				prevIcon.classList.remove('rotate-180');
			}
			const prevPanel = getDesktopMegamenuPanel(currentDesktopToggle);
			if (prevPanel) {
				prevPanel.classList.remove('show');
			}
		}

		if (willOpen) {
			resetDesktopMegamenuFlyouts();
			link.classList.add('show');
			link.setAttribute('aria-expanded', 'true');
			panel.classList.add('show');
			if (icon) {
				icon.classList.add('rotate-180');
			}
			currentDesktopToggle = link;
		} else {
			resetDesktopMegamenuFlyouts();
			link.classList.remove('show');
			link.setAttribute('aria-expanded', 'false');
			panel.classList.remove('show');
			if (icon) {
				icon.classList.remove('rotate-180');
			}
			currentDesktopToggle = null;
		}
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		bindDesktopMegamenuClicks(document.querySelector('.site-header-desktop'));
		bindMobileNavigationToggle();
		initMobileMenuDrilldownStacks();
	});
} else {
	bindDesktopMegamenuClicks(document.querySelector('.site-header-desktop'));
	bindMobileNavigationToggle();
	initMobileMenuDrilldownStacks();
}

document.addEventListener('click', (event) => {
	const target = event.target;
	if (!(target instanceof Element)) {
		return;
	}
	if (target.closest('.site-header-desktop .has-megamenu')) {
		return;
	}
	closeAllDesktopMegaMenus();
	currentDesktopToggle = null;
});

document.addEventListener('keydown', (event) => {
	if (event.key === 'Escape') {
		closeAllDesktopMegaMenus();
		currentDesktopToggle = null;
		closeMobileNavigation();
	}
});

document.addEventListener('close-mobile-navigation', () => {
	closeMobileNavigation();
});

document.querySelectorAll('.megamenu').forEach((panel) => {
	panel.addEventListener('click', (event) => {
		event.stopPropagation();
	});
});

document.querySelectorAll('.site-header-desktop .js-megamenu-flyout').forEach((root) => {
	let hideTimer = null;
	const cancelHideTimer = () => {
		if (hideTimer !== null) {
			window.clearTimeout(hideTimer);
			hideTimer = null;
		}
	};
	const clearFlyout = () => {
		root.querySelectorAll('.megamenu-has-children').forEach((li) => {
			li.classList.remove('is-active');
		});
		root.querySelectorAll('.megamenu-level-three-list').forEach((ul) => {
			ul.classList.remove('is-visible');
		});
		clearMegamenuL3HostSizing(root);
	};
	const scheduleClear = () => {
		cancelHideTimer();
		hideTimer = window.setTimeout(clearFlyout, 140);
	};
	root.addEventListener('mouseenter', cancelHideTimer);
	root.addEventListener('mouseleave', scheduleClear);
	/*
	 * Use mouseover (bubbles) on the flyout root instead of mouseenter on each <li>.
	 * L2 rows are <a> children; relying on li mouseenter is unreliable across browsers.
	 */
	root.addEventListener('mouseover', (event) => {
		const target = event.target;
		if (!(target instanceof Element)) {
			return;
		}
		const li = target.closest('.megamenu-has-children');
		if (!li || !root.contains(li)) {
			return;
		}
		if (li.classList.contains('is-active')) {
			return;
		}
		const id = li.getAttribute('data-megamenu-l2-id');
		if (!id) {
			return;
		}
		cancelHideTimer();
		root.querySelectorAll('.megamenu-has-children').forEach((other) => {
			other.classList.remove('is-active');
		});
		li.classList.add('is-active');
		root.querySelectorAll('.megamenu-level-three-list').forEach((ul) => {
			const match = ul.getAttribute('data-l3-for') === id;
			ul.classList.toggle('is-visible', match);
		});
		syncMegamenuL3HostSizing(root);
	});

	const l3Host = root.querySelector('.megamenu-level-three-host');
	if (l3Host) {
		const l3ResizeObserver = new ResizeObserver(() => {
			syncMegamenuL3HostSizing(root);
		});
		l3Host.querySelectorAll('.megamenu-level-three-list').forEach((ul) => {
			l3ResizeObserver.observe(ul);
		});
	}
});

document.querySelectorAll('.main-mobile-navigation .mobile-menu-drilldown-trigger').forEach((trigger) => {
	trigger.addEventListener('click', function (event) {
		event.preventDefault();
		event.stopPropagation();
		const panel = this.closest('.main-mobile-navigation');
		if (!panel) {
			return;
		}
		const targetPanelId = this.getAttribute('data-target-panel-id');
		activateMobileMenuDrilldownPanel(panel, targetPanelId);
	});
});

document.querySelectorAll('.main-mobile-navigation .mobile-menu-drilldown-back').forEach((backButton) => {
	backButton.addEventListener('click', function (event) {
		event.preventDefault();
		event.stopPropagation();
		const panel = this.closest('.main-mobile-navigation');
		if (!panel) {
			return;
		}
		const targetPanelId = this.getAttribute('data-target-panel-id');
		activateMobileMenuDrilldownPanel(panel, targetPanelId);
	});
});
